import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Ban, Check, RedoAlt } from '@alefeducation/ui-elements-library-icons';
import { ToastType } from '@alefeducation/ui-elements-library-core';
import { ApolloError } from '@apollo/client/errors';
import { TOAST_ADMIN_ID } from './AdminToast';
import { TOGGLE_USER, RESET_MFA } from '../../apollo/mutations';
import { ConfirmModal } from '../../utils/ConfirmModal';
import {
  DISABLE_USER_CONFIRMATION_TITLE,
  DISABLE_USER_TEXT,
  ENABLE_USER_CONFIRMATION,
  ENABLE_USER_TEXT,
  hasMfaEnabledRole,
  RESET_MFA_TEXT,
  SHARED_TENANT,
  MFA_RESET_FAILED,
  MFA_RESET_SUCCESS,
} from '../../utils/constants';
import { resetMFA } from '../../utils/resetMfaUtils';
import { injectAutoHideToastOfType } from '../../utils/Toast';
import { createDynamicApolloClient } from '../../apollo/getApolloClient';
import { AdminUser } from '../../apollo/queries/adminUsers/getAdmins.queries';
import DataGridActions from '../DataGrid/DataGridActions';

export interface AdminUserRowActionsProps {
  row: AdminUser;
  tenantCode: string;
  refetch: () => void;
}

export const AdminActions = ({
  row,
  tenantCode,
  refetch,
}: AdminUserRowActionsProps) => {
  const [isUpdateStatusConfirmOpen, setIsUpdateStatusConfirmOpen] =
    useState(false);

  const dynamicClient = useMemo(() => {
    if (tenantCode) return createDynamicApolloClient(tenantCode);
    return undefined;
  }, [tenantCode]);

  const [toggleUserStatus] = useMutation(TOGGLE_USER, {
    onCompleted: () => {
      refetch();
    },
    onError: (error: ApolloError) => {
      console.log(error);
    },
    client: tenantCode === SHARED_TENANT ? undefined : dynamicClient,
  });

  const [resetUserMfa] = useMutation(RESET_MFA, {
    onCompleted: (data) => {
      if (data?.resetUserMfa?.passed?.length > 0) {
        injectAutoHideToastOfType(
          TOAST_ADMIN_ID,
          MFA_RESET_SUCCESS,
          ToastType.SUCCESS,
        );
      } else {
        injectAutoHideToastOfType(
          TOAST_ADMIN_ID,
          MFA_RESET_FAILED,
          ToastType.ERROR,
        );
      }
    },
    onError: () => {
      injectAutoHideToastOfType(
        TOAST_ADMIN_ID,
        MFA_RESET_FAILED,
        ToastType.ERROR,
      );
    },
    client: dynamicClient,
  });

  const updateStatus = () => {
    toggleUserStatus({
      variables: { id: row.uuid, status: !row.enabled },
    });
    setIsUpdateStatusConfirmOpen(false);
  };

  const actions = [
    {
      name: row.enabled ? DISABLE_USER_TEXT : ENABLE_USER_TEXT,
      icon: row.enabled ? <Ban /> : <Check />,
      callback: () => setIsUpdateStatusConfirmOpen(true),
      shouldRender: true,
      testId: row.enabled
        ? `disable-user-button-${row.uuid}`
        : `enable-user-button-${row.uuid}`,
    },
    {
      name: RESET_MFA_TEXT,
      icon: <RedoAlt />,
      callback: () =>
        [SHARED_TENANT, ''].includes(tenantCode)
          ? resetMFA(row.uuid, TOAST_ADMIN_ID)
          : resetUserMfa({ variables: { userIds: [row.uuid] } }),
      shouldRender: true,
      isDisabled: !hasMfaEnabledRole(row.roles),
      testId: `reset-mfa-user-button-${row.uuid}`,
    },
  ];

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <DataGridActions row={row} actions={actions} />
      <ConfirmModal
        open={isUpdateStatusConfirmOpen}
        heading=""
        message={
          row.enabled
            ? DISABLE_USER_CONFIRMATION_TITLE
            : ENABLE_USER_CONFIRMATION
        }
        modalVisibility={setIsUpdateStatusConfirmOpen}
        onYesClick={updateStatus}
      />
    </div>
  );
};
